import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import {
	Autocomplete,
	Box,
	Button,
	FormHelperText,
	Grid,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useTheme,
	useMediaQuery,
} from '@mui/material';

// import PersonIcon from '@mui/icons-material/Person';
// import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';

import airportsOptions from './airports_options.json';
import { ImageUploader } from './ImageUploader';

import {
	BaggageEnum,
	ICodeName,
	IFlightService,
	IFlightServiceLeg,
	IFlightServicePassengersByPassengerType,
	ISelectOption,
	IServiceCostVariationLogs,
	PassengerTypeEnum,
	ServiceUnitEnum,
	TypeServiceEnum,
} from 'features/salesOrder/types';
import { ControlledTextField } from 'features/common/components/ControlledTextField';
import { checkShowErrorMessage, nanoidGenerator } from 'features/common/helpers';
import { ControlledSelect } from 'features/common/components/ControlledSelect';
// import { ControlledCheckBox } from 'features/common/components/ControlledCheckBox';
import {
	getCostVariationLogsByService,
	getOIDIssues,
	parseGDSAirRawDataSaleOrder,
	putFlightLeg,
	sendFlightServiceIssueRequest,
} from 'features/salesOrder/services';
import { IValidatingCarrier } from 'features/transactions/types';

import { FlightsTable } from './FlightsTable';
import { AutocompleteProviders } from 'features/common/components/AutocompleteProviders';
import { gdsOptions, noAplicaGenerico } from 'features/salesOrder/constants';
import { ControlledDateTimePicker } from 'features/common/components/ControlledDateTimePicker';
import { useStyles } from 'features/salesOrder/components/SalesOrderServiceForm/styles';
import {
	calculatMarkupPercentaje,
	createNewPassengerGroup,
	filterServiceCostVariationLogs,
} from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import { ServiceCostVariationLogs } from 'features/salesOrder/components/SalesOrderServiceForm/components/ServiceCostVariationLogs';
import { ShowAlertState } from 'features/common/types';
import { useSaleOrderId } from '../../../context/SaleOrderScopeContext';
import { useServiceQuickEdit } from '../../../context/ServiceQuickEditContext';
import { executeServicePut } from '../../../helpers/executeServicePut';
import { ControlledCheckBox } from '../../../../../../features/common/components/ControlledCheckBox';
import { getRates } from 'features/stats/services';
import { useServiceMarketCountry } from '../../../hooks/useServiceMarketCountry';
import { FlightServicePassengerGroup } from './FlightServicePassengerGroup';
import IssueRequestButton from '../../IssueRequestButton';
import IssueDetailButton from '../../IssueDetailButton';
import { extractErrorMessage } from '../../../../../../features/quotation/helpers';
import { IssueCostCalculatorModal } from './IssueCostCalculatorModal';
import ServiceBillingStatusDetail from '../ServiceBillingStatusDetail';
import ServicePurchaseOrderDetail from '../ServicePurchaseOrderDetail';
import { ProviderPaymentDateSection } from '../ProviderPaymentDateSection';
import { SERVICE_FORM_INITIAL_FOCUS_ID } from '../../../helpers';
import {
	applyProviderPaymentDateFieldsForPut,
	hydrateProviderPaymentDateFormFields,
} from '../ProviderPaymentDateSection/providerPaymentDateFormHelpers';
import { useServiceProviderPaymentConstraints } from '../../../hooks/useServiceProviderPaymentConstraints';
import ProviderPaymentConstraintsAlert from '../ProviderPaymentConstraintsAlert';
import ReferenceValidationAlert from '../ReferenceValidationAlert';
import {
	getTariffProviderIdFromFormData,
	getTariffProviderIdFromService,
	validateProviderPaymentConstraintsBeforeSave,
} from '../../../helpers/providerPaymentConstraints';

interface OpcionAirport {
	label: string;
	id: string;
}

interface Props {
	index: number;
	passengers: { keyId: string; name: string; lastName: string }[];
	service: IFlightService;
	currencyesList: ICodeName[];
	validatingCarriers: IValidatingCarrier[];
	onUpdateService: () => void;
	onSetAlert: (values: ShowAlertState) => void;
	onSetLoading: (value: boolean) => void;
	isExistingService?: boolean;
}
// const iconMapping = {
// 	Persona: <PersonIcon />,
// 	Grupo: <GroupIcon />,
// };

interface DestinationDetail {
	airportCode: string | null;
	date: string; // Puede ser más preciso con un tipo Date si lo prefieres
	hour: string;
}
export interface FlightLeg {
	flightNumber: string;
	airlineCode: string;
	departure: DestinationDetail;
	arrival: DestinationDetail;
}

const initialFlightLeg = {
	flightNumber: '',
	airlineCode: 'noselection',
	departure: { airportCode: 'noselection', date: '', hour: '' },
	arrival: { airportCode: 'noselection', date: '', hour: '' },
};

const isArrivalBeforeDeparture = (
	departureDate: string,
	departureTime: string,
	arrivalDate: string,
	arrivalTime: string,
): boolean => {
	// Combinar las fechas y horas en un solo string
	const departureDateTimeString = `${departureDate}T${departureTime}:00`;
	const arrivalDateTimeString = `${arrivalDate}T${arrivalTime}:00`;

	// Crear objetos Date a partir de los strings combinados
	const departureDateTime = new Date(departureDateTimeString);
	const arrivalDateTime = new Date(arrivalDateTimeString);

	// Comparar las dos fechas
	return departureDateTime > arrivalDateTime;
};

export const FlightService = ({
	index,
	passengers,
	service,
	// currencyesList,
	validatingCarriers,
	onUpdateService,
	onSetAlert,
	onSetLoading,
	isExistingService,
}: Props): JSX.Element => {
	const classes: any = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const methods = useForm();
	// 	{
	// 	defaultValues: {
	// 		//@ts-ignore
	// 		passengersTickets: [],
	// 	},
	// }
	const {
		control,
		formState: { errors },
		setValue,
		getValues,
		reset,
		resetField,
		watch,
	} = methods;

	const {
		fields: passengersGroupByPassengerType,
		append,
		replace,
		remove,
	} = useFieldArray<IFlightServicePassengersByPassengerType>({
		// @ts-ignore
		control,
		// @ts-ignore
		name: 'passengersGroupByPassengerType',
	});

	const [flightLeg, setFlightLeg] = useState<FlightLeg>({ ...initialFlightLeg });
	const [initialPassengersGroup, setInitialPassengersGroup] = useState<IFlightServicePassengersByPassengerType[]>(
		service.passengersGroupByPassengerType,
	);

	const theCountry = useServiceMarketCountry();
	const paymentConstraints = useServiceProviderPaymentConstraints(service.keyId);
	const initialTariffProviderId = useMemo(() => getTariffProviderIdFromService(service), [service]);

	const handleFlightNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFlightLeg({ ...flightLeg, flightNumber: event.target.value });
	};

	const handleAirlineCodeChange = (event: SelectChangeEvent<string>) => {
		setFlightLeg({ ...flightLeg, airlineCode: event.target.value as string });
	};
	const handleDepartureAirportCodeChange = (
		_event: React.SyntheticEvent<Element, Event>,
		newValue: OpcionAirport | null,
	) => {
		// setFlightLeg({ ...flightLeg, airlineCode: event.target.value as string });
		if (newValue) {
			setFlightLeg({
				...flightLeg,
				departure: { ...flightLeg.departure, airportCode: newValue.id },
			});
		}
	};

	const handleDepartureDateTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const [date, hour] = event.target.value.split('T');
		setFlightLeg({
			...flightLeg,
			departure: { ...flightLeg.departure, date, hour },
		});
	};

	const handleArrivalAirportCodeChange = (
		_event: React.SyntheticEvent<Element, Event>,
		newValue: OpcionAirport | null,
	) => {
		// setFlightLeg({ ...flightLeg, airlineCode: event.target.value as string });
		if (newValue) {
			setFlightLeg({
				...flightLeg,
				arrival: { ...flightLeg.arrival, airportCode: newValue.id },
			});
		}
	};

	const handleArrivalDateTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const [date, hour] = event.target.value.split('T');
		setFlightLeg({
			...flightLeg,
			arrival: { ...flightLeg.arrival, date, hour },
		});
	};

	const [serviceCostVariationLogs, setServiceCostVariationLogs] = useState<IServiceCostVariationLogs[]>([]);
	const [issueCostModalOpen, setIssueCostModalOpen] = useState(false);

	const saleOrderId = useSaleOrderId();
	const quickEdit = useServiceQuickEdit();
	const [oidIssuesList, setOidIssuesList] = useState<ISelectOption[]>([{ ...noAplicaGenerico }]);
	const [gdsRawData, setGdsRawData] = useState('');
	const [flightLegs, setFlightLegs] = useState<IFlightServiceLeg[]>([]);
	const idService = service.keyId;

	const filteredArray = filterServiceCostVariationLogs(serviceCostVariationLogs, idService);

	const unit = watch('unit' as `${string}.${string | number}`);
	const withTst = watch('withTst');

	const handleAddLeg = async () => {
		if (flightLeg.airlineCode === 'noselection') {
			alert('Tiene que elegir una aerolinea');
			return false;
		}

		if (flightLeg.departure.airportCode === 'noselection') {
			alert('Tiene que elegir un aeropuerto de salida');
			return false;
		}

		if (flightLeg.arrival.airportCode === 'noselection') {
			alert('Tiene que elegir un aeropuerto de llegada');
			return false;
		}

		if (flightLeg.flightNumber === '') {
			alert('Tienes que ingresar un numero de vuelo');
			return false;
		}

		if (
			isArrivalBeforeDeparture(
				flightLeg.departure.date,
				flightLeg.departure.hour,
				flightLeg.arrival.date,
				flightLeg.arrival.hour,
			)
		) {
			alert('La fecha de llegada no puede ser anterior a la fecha de salida');
			return false;
		}

		const data = { ...flightLeg, keyId: nanoidGenerator() };
		try {
			await putFlightLeg(saleOrderId, idService, data);
			onSetAlert({
				show: true,
				severity: 'success',
				message: 'Pierna Agregada',
			});
			setFlightLeg({
				...flightLeg,
				flightNumber: '',
				departure: { ...flightLeg.departure, airportCode: null },
				arrival: { ...flightLeg.arrival, airportCode: null },
			});
		} catch (error) {
			// @ts-ignore
			alert(error.response.data.message);
		} finally {
			setTimeout(() => {
				onSetLoading(false);
				onUpdateService();
			}, 500);
		}
	};

	const handleGdsRawDataChange = (inputValue: string) => {
		setGdsRawData(inputValue);
	};

	// Función para manejar cuando se genera un TST desde las imágenes
	const handleTstGenerated = (tst: string) => {
		setValue('tst' as `${string}.${string | number}`, tst);
		handleGdsRawDataChange(tst);
	};

	const handleAddTST = async () => {
		try {
			onSetLoading(true);
			const response = await parseGDSAirRawDataSaleOrder(gdsRawData);
			const result = response.data;

			if (Array.isArray(result) && result?.length > 0) {
				setFlightLegs(result);

				onSetAlert({
					show: true,
					severity: 'success',
					message: 'TST agregado',
				});
			}
		} catch (error) {
			// @ts-ignore
			alert(error.response.data.message);
		} finally {
			onSetLoading(false);
		}
	};

	const loadOidIssues = useCallback(async () => {
		try {
			onSetLoading(true);
			const oidOptionsResponse = (await getOIDIssues()).data;
			setOidIssuesList([{ ...noAplicaGenerico }, ...oidOptionsResponse]);
		} catch (error) {
			// @ts-ignore
			alert(error);
		} finally {
			onSetLoading(false);
		}
	}, [onSetLoading]);

	const loadCostLog = useCallback(async () => {
		const result = await getCostVariationLogsByService(saleOrderId, idService, quickEdit?.agencyId);
		setServiceCostVariationLogs(result.data);
	}, [saleOrderId, idService, quickEdit?.agencyId]);

	useEffect(() => {
		loadOidIssues();
		loadCostLog();
	}, [loadOidIssues, loadCostLog]);

	const handleUpdateService = async (data: IFlightService) => {
		try {
			onSetLoading(true);

			const flightService = data as IFlightService;

			if (
				!validateProviderPaymentConstraintsBeforeSave({
					constraints: paymentConstraints,
					totalCost: Number(flightService.totalCost),
					initialTariffProviderId,
					submittedTariffProviderId: getTariffProviderIdFromFormData(flightService),
					onSetAlert,
					onSetLoading,
				})
			) {
				return;
			}

			// @ts-ignore
			flightService.markupPercentage = Number(flightService.markupPercentage);
			if (flightService.gds === 'n/a') {
				delete flightService.gds;
			}
			if (flightService.oidIssueId === 'n/a') {
				delete flightService.oidIssueId;
			}

			if (flightService.tariffProvider === 'n/a') {
				delete flightService.tariffProvider;
			} else if (typeof flightService.tariffProvider === 'string') {
				// @ts-ignore
				flightService.tariffProviderId = flightService.tariffProvider;
				delete flightService.tariffProvider;
			}
			// @ts-ignore
			if (flightService.tariffProviderId === 'n/a') {
				// @ts-ignore
				delete flightService.tariffProviderId;
			}

			if (flightService.flightLegs) {
				for (let flightLegIndex = 0; flightLegIndex < flightService.flightLegs.length; flightLegIndex++) {
					if (Boolean(flightService.flightLegs[flightLegIndex].keyId) === false) {
						flightService.flightLegs[flightLegIndex].keyId = nanoidGenerator();
					}
				}
			}

			if (flightLegs) {
				flightService.flightLegs = flightLegs.map((flightLeg) => ({
					...flightLeg,
					keyId: nanoidGenerator(),
				}));
			}

			flightService.serviceType = TypeServiceEnum.Flight;

			if (theCountry !== 'CO') {
				// @ts-ignore
				delete flightService.currencyRate;
				// @ts-ignore
				delete flightService.totalSaleAmountInLocalCurrency;
			}

			if (flightService.withTst === 'manual') {
				flightService.flightLegs = [];
				// @ts-ignore
				flightService.withTst = false;
				// @ts-ignore
				delete flightService.tst;
			} else {
				// @ts-ignore
				flightService.withTst = true;
			}

			if (Number(flightService.totalSaleAmount) == 0) {
				// @ts-ignore
				delete flightService.markupPercentage;
			} else {
				// @ts-ignore
				flightService.markupPercentage = parseFloat(
					calculatMarkupPercentaje(Number(flightService.totalCost), Number(flightService.totalSaleAmount)),
				);
			}

			flightService.passengersGroupByPassengerType = flightService.passengersGroupByPassengerType.map(
				(passengerGroup) => {
					if (passengerGroup.saleAmount === 0) {
						const group = { ...passengerGroup };
						delete group.markupPercentage;
						return group;
					} else return passengerGroup;
				},
			);

			applyProviderPaymentDateFieldsForPut(flightService, {
				formData: data,
				service,
				getValues,
			});

			const saved = await executeServicePut({
				saleOrderId,
				serviceId: flightService.keyId,
				data: flightService,
				quickEdit,
			});
			if (!saved) {
				onSetLoading(false);
				return;
			}
			if (!quickEdit) {
				onSetAlert({
					show: true,
					severity: 'success',
					message: 'Servicio guardado con exito',
				});
			}
		} catch (error) {
			// @ts-ignore
			if (error.response) {
				// @ts-ignore
				alert(error.response.data.message);
			} else {
				console.log('error', error);
			}
		} finally {
			onSetLoading(false);
			if (!quickEdit) {
				onUpdateService();
			}
		}
	};

	useEffect(() => {
		// Solo reinicializar si el keyId del servicio cambió (servicio diferente)
		// Esto evita reinicializar cuando solo se actualizan los datos del mismo servicio
		if (service.keyId) {
			reset();
			remove();
			setFlightLegs([]);

			resetField('limitDate');

			const input = document.querySelector('input[name="limitDate"]');
			if (input) {
				// @ts-ignore
				input.value = '';
			}

			const effectTariffProviderId =
				typeof service.tariffProvider === 'string'
					? service.tariffProvider || 'n/a'
					: service.tariffProvider?.id || 'n/a';
			setValue('keyId' as `${string}.${string | number}`, service.keyId);
			setValue('reference' as `${string}.${string | number}`, service.reference || '');
			// comente esto rene setValue('passengerIds' as `${string}.${string | number}`, service.passengerIds || '');

			setValue('gds' as `${string}.${string | number}`, service.gds ? service.gds : 'n/a');
			setValue('tariffProvider' as `${string}.${string | number}`, effectTariffProviderId);
			setValue('vc' as `${string}.${string | number}`, service.vc || '');
			setValue('carryOn' as `${string}.${string | number}`, service.carryOn || false);
			setValue('currency' as `${string}.${string | number}`, service.currency || 'USD');
			setValue(
				'baggageType' as `${string}.${string | number}`,
				service.baggageType ? service.baggageType : BaggageEnum.None,
			);
			setValue('oidIssueId' as `${string}.${string | number}`, service.oidIssueId ? service.oidIssueId : 'n/a');
			// @ts-ignore
			setValue('limitDate', service.limitDate || '');
			setValue(
				'unit' as `${string}.${string | number}`,
				service.unit.length === 0 ? ServiceUnitEnum.Person : service.unit,
			);
			setValue('totalCost' as `${string}.${string | number}`, service.totalCost || 0);
			setValue('totalSaleAmount' as `${string}.${string | number}`, service.totalSaleAmount || 0);
			setValue('tst' as `${string}.${string | number}`, service.tst || '');
			setValue('tqt' as `${string}.${string | number}`, service.tqt || '');
			setValue('voucherNotes' as `${string}.${string | number}`, service.voucherNotes || '');
			setValue('internalNotes' as `${string}.${string | number}`, service.internalNotes || '');
			// Para servicios nuevos, usar 'tst' por defecto; para existentes, preservar el valor
			let withTstValue = 'manual';
			if (!isExistingService) {
				withTstValue = 'tst';
			} else if ((service.withTst as any) === true) {
				withTstValue = 'tst';
			}
			// @ts-ignore
			setValue('withTst' as `${string}.${string | number}`, withTstValue);

			setValue('FXL', service.FXL || false);
			setValue('OPEROK', service.OPEROK || false);
			setValue('isReissue', service.isReissue || false);
			if (theCountry === 'CO') {
				if (!isExistingService) {
					const fetchRates = async () => {
						const result = await getRates();
						setValue('currencyRate', result.data.ratesList[0].values[0].effectiveRate);
					};
					fetchRates();
				} else {
					setValue('currencyRate', service.currencyRate || 1);
				}
				setValue('totalSaleAmountInLocalCurrency', service.totalSaleAmountInLocalCurrency || 1);
			}

			if (service.flightLegs?.length > 0) {
				setFlightLegs(service.flightLegs);
			}

			hydrateProviderPaymentDateFormFields(setValue, service);
		}
	}, [
		service.keyId,
		service.gds,
		service.vc,
		service.oidIssueId,
		service.withTst,
		service.tariffProvider,
		setValue,
		index,
		reset,
		append,
		remove,
		isExistingService,
		theCountry,
	]);

	const paymentDateHydrateKey = useMemo(() => {
		const lines = service?.providerManualPaymentDateLines ?? [];
		const linesKey = lines
			.map((l) => `${l.id ?? 'n'}|${l.paymentDate ?? ''}|${l.amount ?? ''}|${l.observation ?? ''}`)
			.join('~');

		return `${service?.keyId ?? 'no'}|${service?.automaticProviderPaymentDateCalculation === true}|${
			lines.length
		}|${linesKey}`;
	}, [service?.keyId, service?.automaticProviderPaymentDateCalculation, service?.providerManualPaymentDateLines]);

	useEffect(() => {
		if (!service?.keyId) return;
		hydrateProviderPaymentDateFormFields(setValue, service);
	}, [paymentDateHydrateKey, setValue, service]);

	useEffect(() => {
		setValue('currency' as `${string}.${string | number}`, 'USD');
		setValue('carryOn' as `${string}.${string | number}`, false);
	}, [setValue]);

	useEffect(() => {
		setInitialPassengersGroup([...service.passengersGroupByPassengerType]);
		// @ts-ignore
		replace(service.passengersGroupByPassengerType);
	}, [service.keyId, service.passengersGroupByPassengerType, replace]);

	const handleAddPassengerGroup = (passengerType: PassengerTypeEnum) => {
		const newPassengerGroup = createNewPassengerGroup(passengerType);
		setInitialPassengersGroup((prev) => {
			return [...prev, newPassengerGroup];
		});
		// @ts-ignore
		append(newPassengerGroup);
	};

	const handleRemovePassengerGroup = (index: number) => {
		setInitialPassengersGroup((prev) => {
			const updated = [...prev];
			updated.splice(index, 1);
			return updated;
		});

		remove(index);
	};

	const existBGroup = initialPassengersGroup.some((item) => item.passengerType === PassengerTypeEnum.B);
	const existCGroup = initialPassengersGroup.some((item) => item.passengerType === PassengerTypeEnum.C);
	const existDGroup = initialPassengersGroup.some((item) => item.passengerType === PassengerTypeEnum.D);

	const currencyRateValue = useWatch({
		control,
		name: 'currencyRate',
	});

	const referenceValue = useWatch({ control, name: 'reference' }) as string;
	const tariffProviderWatched = useWatch({ control, name: 'tariffProvider' }) as string;

	const passengersGroupByPassengerTypeValue = useWatch({
		control,
		name: 'passengersGroupByPassengerType',
	});

	const totalCostSum =
		passengersGroupByPassengerTypeValue?.reduce(
			(sum: any, group: { totalCost: any }) => sum + (group.totalCost || 0),
			0,
		) || 0;

	const totalSaleAmountSum =
		passengersGroupByPassengerTypeValue?.reduce(
			(sum: any, group: { totalSaleAmount: any }) => sum + (group.totalSaleAmount || 0),
			0,
		) || 0;

	const totalSaleAmountSumInLocalCurrency =
		passengersGroupByPassengerTypeValue?.reduce(
			(sum: any, group: { totalSaleAmountInLocalCurrency: any }) => sum + (group.totalSaleAmountInLocalCurrency || 0),
			0,
		) || 0;

	const currentTotalCost = getValues('totalCost' as any);
	const currentTotalSaleAmount = getValues('totalSaleAmount' as any);

	if (currentTotalCost !== totalCostSum) {
		setValue('totalCost' as `${string}.${string | number}`, totalCostSum);
	}
	if (currentTotalSaleAmount !== totalSaleAmountSum) {
		setValue('totalSaleAmount' as `${string}.${string | number}`, totalSaleAmountSum);
	}

	useEffect(() => {
		if (theCountry === 'CO') {
			setValue('totalSaleAmountInLocalCurrency', Number(totalSaleAmountSumInLocalCurrency));
		}
	}, [currencyRateValue, totalSaleAmountSum, totalCostSum]);

	const isButtonIssueRequestEnabled = isExistingService && service.reference && service.reference !== '-';

	const handleSendIssueRequest = async () => {
		try {
			onSetLoading(true);
			await sendFlightServiceIssueRequest(saleOrderId, idService);
			onSetAlert({
				show: true,
				severity: 'success',
				message: 'Solicitud de emisión enviada satisfactoriamente',
			});
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Error enviando la solicitud de emisión'),
			});
		} finally {
			setTimeout(() => {
				onSetLoading(false);
				onUpdateService();
			}, 500);
		}
	};

	const handleIssueCostSave = async (updatedGroups: IFlightServicePassengersByPassengerType[]) => {
		try {
			onSetLoading(true);

			// Crear servicio actualizado manteniendo la estructura original
			const updatedService: IFlightService = {
				...service,
				passengersGroupByPassengerType: updatedGroups,
			};

			// Procesar tariffProvider de la misma manera que en handleUpdateService
			const processedService = { ...updatedService };

			if (processedService.tariffProvider === 'n/a') {
				delete processedService.tariffProvider;
			} else if (typeof processedService.tariffProvider === 'object' && processedService.tariffProvider?.id) {
				// @ts-ignore
				processedService.tariffProviderId = processedService.tariffProvider.id;
				delete processedService.tariffProvider;
			}

			// Recalcular totales
			const totalCostSum = updatedGroups.reduce((sum, group) => sum + (group.totalCost || 0), 0);
			const totalSaleAmountSum = updatedGroups.reduce((sum, group) => sum + (group.totalSaleAmount || 0), 0);

			if (
				!validateProviderPaymentConstraintsBeforeSave({
					constraints: paymentConstraints,
					totalCost: totalCostSum,
					initialTariffProviderId,
					submittedTariffProviderId: getTariffProviderIdFromService(processedService),
					onSetAlert,
					onSetLoading,
				})
			) {
				return;
			}

			processedService.totalCost = totalCostSum.toString();
			processedService.totalSaleAmount = totalSaleAmountSum.toString();

			const saved = await executeServicePut({
				saleOrderId,
				serviceId: processedService.keyId,
				data: processedService,
				quickEdit,
			});
			if (!saved) {
				onSetLoading(false);
				return;
			}

			// Actualizar el estado local después de guardar exitosamente
			setInitialPassengersGroup(updatedGroups);
			// @ts-ignore
			replace(updatedGroups);

			// Actualizar los valores del formulario
			setValue('totalCost' as `${string}.${string | number}`, totalCostSum);
			setValue('totalSaleAmount' as `${string}.${string | number}`, totalSaleAmountSum);

			if (!quickEdit) {
				onSetAlert({
					show: true,
					severity: 'success',
					message: 'Costos de emisión actualizados correctamente',
				});
			}
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Error actualizando los costos de emisión',
			});
		} finally {
			onSetLoading(false);
		}
	};

	return (
		<FormProvider {...methods}>
			<form
				onSubmit={methods.handleSubmit((data) => {
					handleUpdateService(data as IFlightService);
				})}
			>
				<div>
					<Grid container spacing={2} className={classes.form}>
						<Grid item xs={12} sm={12}>
							<Typography variant="h6">Datos de la reserva</Typography>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Referencia</Typography>
							<ControlledTextField
								name={'reference'}
								inputProps={!isExistingService ? { id: SERVICE_FORM_INITIAL_FOCUS_ID } : undefined}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								helperText={checkShowErrorMessage(Boolean(errors.reference), errors.reference?.message)}
							/>
						</Grid>
						{tariffProviderWatched && tariffProviderWatched !== 'n/a' && (
							<Grid item xs={12}>
								<ReferenceValidationAlert providerId={tariffProviderWatched} reference={referenceValue ?? ''} />
							</Grid>
						)}
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>GDS</Typography>
							<ControlledSelect
								name={'gds'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={gdsOptions}
							/>
						</Grid>
						<Grid item xs={12}>
							<ProviderPaymentConstraintsAlert constraints={paymentConstraints} />
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Proveedor de tarifas</Typography>
							<AutocompleteProviders
								allowNotApplicable
								name={'tariffProvider'}
								disabled={paymentConstraints.lockTariffProvider}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Validating carrier</Typography>
							<Controller
								control={control}
								// @ts-ignore
								name={'vc'}
								rules={{ required: 'Este campo es requerido' }}
								render={({ field }) => (
									<>
										<Select
											className={classes.selected}
											{...field}
											size={'small'}
											value={field.value ? field.value.code : ''}
											onChange={(event) => {
												const selectedValue = event.target.value;
												const selectedItem = validatingCarriers?.find((item) => item.code === selectedValue);
												field.onChange(selectedItem);
											}}
										>
											{validatingCarriers?.map((item) => (
												<MenuItem key={item.code} value={item.code}>
													{item.name}
												</MenuItem>
											))}
										</Select>
										{errors['vc'] && <FormHelperText sx={{ color: 'red' }}>{errors['vc']?.message}</FormHelperText>}
									</>
								)}
							/>
						</Grid>

						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>OID de emision</Typography>
							<ControlledSelect
								name={'oidIssueId'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={oidIssuesList?.map((item) => ({ id: item?.id?.toString(), name: item?.name }))}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Fecha limite</Typography>
							<ControlledDateTimePicker
								name={'limitDate'}
								rules={{ required: 'Este campo es requerido' }}
								required={true}
							/>
						</Grid>

						{/* Sugerencia de emisión */}
						{isExistingService && service.serviceType === 'Flight' && service.ruleComment && (
							<Grid item xs={12}>
								<Box
									display="flex"
									gap={2}
									alignItems={isMobile ? 'stretch' : 'center'}
									flexDirection={isMobile ? 'column' : 'row'}
								>
									<Box
										sx={{
											backgroundColor: '#f5f5f5',
											border: '1px solid #e0e0e0',
											borderRadius: 1,
											p: 2,
											mt: 1,
											mb: 1,
											flex: 1,
										}}
									>
										<Typography variant="subtitle2" color="primary" gutterBottom>
											💡 Observación para emisión
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{service.ruleComment}
										</Typography>
									</Box>
									<Button
										variant="outlined"
										size="small"
										onClick={() => setIssueCostModalOpen(true)}
										sx={{
											minWidth: isMobile ? '100%' : 180,
											alignSelf: isMobile ? 'stretch' : 'center',
										}}
									>
										Aplicar costos de emisión
									</Button>
								</Box>
							</Grid>
						)}

						<Grid item xs={12}>
							{passengersGroupByPassengerType.map((_passengerGroup, passengerGroupByPassengerTypeIndex) => (
								<React.Fragment
									key={`passenger-group-${initialPassengersGroup[passengerGroupByPassengerTypeIndex].passengerType}`}
								>
									<FlightServicePassengerGroup
										unit={unit}
										allPassengers={passengers}
										value={initialPassengersGroup[passengerGroupByPassengerTypeIndex]}
										index={passengerGroupByPassengerTypeIndex}
										handleRemovePassengerGroup={handleRemovePassengerGroup}
										isLast={passengerGroupByPassengerTypeIndex === initialPassengersGroup.length - 1}
										existBGroup={existBGroup}
										existCGroup={existCGroup}
										existDGroup={existDGroup}
										handleAddPassengerGroup={handleAddPassengerGroup}
									/>
								</React.Fragment>
							))}
						</Grid>
						<Grid item xs={12} sm={12}>
							<Typography variant="h6"> Valores totales de referencia:</Typography>
						</Grid>
						{/* <Grid item xs={12} sm={3}>
							<Typography color={'Grey'}>Unidad por</Typography>
							<ControlledSelect
								name={'unit'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={excludeTypeUnits(typeUnit, ['Habitacion', 'Vehiculo', 'Camarote'])}
								iconTypeTrip={iconMapping}
							/>
						</Grid> */}

						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Moneda</Typography>
							<Typography color={'Grey'}>USD (Dolares Americanos)</Typography>
							{/* <ControlledSelect
								name={'currency'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={currencyesList.map((item) => ({ id: item.code.toString(), name: item.code }))}
							/> */}
						</Grid>

						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Costo Total</Typography>
							<ControlledTextField name={'totalCost'} disabled />
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Venta Total</Typography>
							<ControlledTextField name={'totalSaleAmount'} disabled />
						</Grid>

						{theCountry === 'CO' && (
							<>
								<Grid item xs={12} sm={4} />
								<Grid item xs={12} sm={4}>
									<Typography color={'Grey'}>Tipo de Cambio</Typography>
									<ControlledTextField name={'currencyRate'} disabled={isExistingService} />
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography color={'Grey'}>Total en COP</Typography>
									<ControlledTextField name={'totalSaleAmountInLocalCurrency'} disabled />
								</Grid>
							</>
						)}
						<Grid item xs={12} sm={12}>
							<Typography variant="h6"> Controles de operaciones:</Typography>
						</Grid>
						{
							<Grid item xl={2} xs={3} sm={6}>
								<ControlledCheckBox name={'FXL'} label="FXL" onChange={(isChecked) => setValue('FXL', isChecked)} />
							</Grid>
						}
						{
							<Grid item xl={2} xs={3} sm={6}>
								<ControlledCheckBox
									name={'OPEROK'}
									label="OPEROK"
									onChange={(isChecked) => setValue('OPEROK', isChecked)}
								/>
							</Grid>
						}
						{
							<Grid item xl={2} xs={3} sm={6}>
								<ControlledCheckBox
									name={'isReissue'}
									label="Es reemisión"
									onChange={(isChecked) => setValue('isReissue', isChecked)}
								/>
							</Grid>
						}
						<Grid item xs={12} sm={12}>
							<Typography variant="h6"> Itinerario:</Typography>
						</Grid>

						{/* Mostrar itinerario existente justo después del título */}
						{flightLegs && flightLegs.length > 0 && (
							<Grid item xs={12}>
								<FlightsTable
									flightLegs={flightLegs}
									saleOrderId={saleOrderId}
									serviceId={idService}
									onUpdateService={onUpdateService}
									onSetAlert={onSetAlert}
									onSetLoading={onSetLoading}
									allowDelete={withTst === 'manual'}
								/>
							</Grid>
						)}

						<Grid item xs={12}>
							<Typography color={'Grey'}>Aire con TST o Manual</Typography>
							<ControlledSelect
								name={'withTst'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={[
									{
										id: 'tst',
										name: 'Con Tst',
									},
									{
										id: 'manual',
										name: 'Manual',
									},
								]}
							/>
						</Grid>

						{withTst === 'tst' ? (
							<>
								{/* Layout reorganizado: 1/3 para ImageUploader, 2/3 para TST con alineación bottom */}
								<Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
									<ImageUploader onTstGenerated={handleTstGenerated} onSetAlert={onSetAlert} disabled={false} />
								</Grid>
								<Grid item xs={12} sm={8} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
									<Typography color={'Grey'}>TST</Typography>
									<Controller
										// @ts-ignore
										name={'tst'}
										control={control}
										render={({ field }) => (
											<TextField
												{...field}
												onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
													const inputValue = e.target.value;
													field.onChange(inputValue);
													handleGdsRawDataChange(inputValue);
												}}
												value={field.value}
												fullWidth
												variant="outlined"
												multiline
												rows={10}
												helperText={checkShowErrorMessage(Boolean(errors.internalNotes), errors.internalNotes?.message)}
											/>
										)}
									/>
								</Grid>
							</>
						) : (
							<Grid item xs={12}>
								<Box
									display="flex"
									flexDirection={isMobile ? 'column' : 'row'}
									gap={2}
									mt={2}
									flexWrap={isMobile ? 'nowrap' : 'wrap'}
								>
									<TextField
										size="small"
										label="Numero de vuelo"
										value={flightLeg.flightNumber}
										onChange={handleFlightNumberChange}
										style={{ width: isMobile ? '100%' : 120, minWidth: isMobile ? 'auto' : 80 }}
									/>

									<Select
										size="small"
										value={flightLeg.airlineCode}
										onChange={handleAirlineCodeChange}
										style={{ width: isMobile ? '100%' : 150, minWidth: isMobile ? 'auto' : 120 }}
									>
										<MenuItem key="noselection" value="noselection">
											Aerolinea
										</MenuItem>
										{validatingCarriers.map((option) => (
											<MenuItem key={option.code} value={option.code}>
												{option.name}
											</MenuItem>
										))}
									</Select>

									<Autocomplete
										value={
											flightLeg.departure.airportCode === null
												? null
												: airportsOptions.find((opt) => opt.id === flightLeg.departure.airportCode)
										}
										onChange={handleDepartureAirportCodeChange}
										options={airportsOptions}
										getOptionLabel={(option: OpcionAirport) => option.label}
										renderInput={(params) => (
											<TextField {...params} size="small" label="Salida de" variant="outlined" />
										)}
										style={{ width: isMobile ? '100%' : 250, minWidth: isMobile ? 'auto' : 200 }}
									/>

									<TextField
										size="small"
										label="Fecha y Hora de Salida"
										type="datetime-local"
										InputLabelProps={{ shrink: true }}
										value={`${flightLeg.departure.date}T${flightLeg.departure.hour}`}
										onChange={handleDepartureDateTimeChange}
										style={{ width: isMobile ? '100%' : 200, minWidth: isMobile ? 'auto' : 180 }}
									/>

									<Autocomplete
										value={
											flightLeg.arrival.airportCode === null
												? null
												: airportsOptions.find((opt) => opt.id === flightLeg.arrival.airportCode)
										}
										onChange={handleArrivalAirportCodeChange}
										options={airportsOptions}
										getOptionLabel={(option: OpcionAirport) => option.label}
										renderInput={(params) => (
											<TextField {...params} size="small" label="Llegada a" variant="outlined" />
										)}
										style={{ width: isMobile ? '100%' : 250, minWidth: isMobile ? 'auto' : 200 }}
									/>

									<TextField
										label="Fecha y Hora de Llegada"
										size="small"
										type="datetime-local"
										InputLabelProps={{ shrink: true }}
										value={`${flightLeg.arrival.date}T${flightLeg.arrival.hour}`}
										onChange={handleArrivalDateTimeChange}
										style={{ width: isMobile ? '100%' : 200, minWidth: isMobile ? 'auto' : 180 }}
									/>
								</Box>
							</Grid>
						)}
						{(withTst === 'tst' || withTst === 'manual') && (
							<Grid item xs={12} sm={12}>
								<Button
									type="button"
									fullWidth
									variant="contained"
									onClick={withTst === 'tst' ? handleAddTST : handleAddLeg}
								>
									Agregar Aire
								</Button>
							</Grid>
						)}

						{/* Mostrar tabla de vuelos solo cuando no hay vuelos existentes al inicio */}
						{flightLegs && flightLegs.length === 0 && (
							<FlightsTable
								flightLegs={flightLegs}
								saleOrderId={saleOrderId}
								serviceId={idService}
								onUpdateService={onUpdateService}
								onSetAlert={onSetAlert}
								onSetLoading={onSetLoading}
								allowDelete={withTst === 'manual'}
							/>
						)}
						<Grid item xs={12} sm={12}>
							<Typography color={'Grey'}>TQT</Typography>
							<Controller
								// @ts-ignore
								name={'tqt'}
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
											const inputValue = e.target.value;
											field.onChange(inputValue);
										}}
										value={field.value}
										fullWidth
										variant="outlined"
										multiline
										rows={10}
										helperText={checkShowErrorMessage(Boolean(errors.internalNotes), errors.internalNotes?.message)}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12}>
							<Typography color={'Grey'}>Notas de voucher</Typography>
							<Controller
								// @ts-ignore
								name={'voucherNotes'}
								control={control}
								defaultValue="Favor de presentar voucher en el check-in"
								render={({ field }) => (
									<TextField
										fullWidth
										variant="outlined"
										multiline
										rows={5}
										{...field}
										helperText={checkShowErrorMessage(Boolean(errors.voucherNotes), errors.voucherNotes?.message)}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12}>
							<Typography color={'Grey'}>
								<LockIcon /> Notas internas
							</Typography>
							<Controller
								// @ts-ignore
								name={'internalNotes'}
								control={control}
								render={({ field }) => (
									<TextField
										fullWidth
										variant="outlined"
										multiline
										rows={5}
										{...field}
										helperText={checkShowErrorMessage(Boolean(errors.internalNotes), errors.internalNotes?.message)}
									/>
								)}
							/>
						</Grid>
						<ServiceCostVariationLogs filteredArray={filteredArray} />
					</Grid>

					<ServiceBillingStatusDetail billingStatusDetail={service.billingStatusDetail} />
					<ServicePurchaseOrderDetail serviceId={service.keyId} serviceType={service.serviceType} />
					<ProviderPaymentDateSection
						hydrateKey={paymentDateHydrateKey}
						control={control}
						watch={watch}
						setValue={setValue}
						getValues={getValues}
						clearErrors={methods.clearErrors}
						resetField={resetField}
						errors={errors}
						servicePath={''}
						providerIdPath={'tariffProvider'}
						providerAutomaticPaymentDateLine={service.providerAutomaticPaymentDateLine}
					/>

					{
						<Box display="flex" flexDirection="row-reverse" mt={2} gap={2}>
							{
								// @ts-ignore
								(service.enable || service.enable === undefined) && (
									<Button type="submit" variant="contained" color="success">
										Guardar
									</Button>
								)
							}
							<IssueRequestButton
								showButton={!service.issueRequest}
								disabled={!isButtonIssueRequestEnabled}
								handleClick={handleSendIssueRequest}
							/>
							<IssueDetailButton issueRequestDetail={service.issueRequest} />
						</Box>
					}
				</div>
			</form>

			{/* Modal de cálculo de costos de emisión */}
			<IssueCostCalculatorModal
				open={issueCostModalOpen}
				onClose={() => setIssueCostModalOpen(false)}
				service={service}
				onSave={handleIssueCostSave}
				oidIssuesList={oidIssuesList}
			/>
		</FormProvider>
	);
};
